import React, { useState } from 'react';
import flightSearchService from '../../../services/flightSearchService';
import BeforeYouBook from '../../../components/BeforeYouBook';

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/no_aircraft.png';

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  return `${baseUrl}/${imageUrl}`;
};

const FlightCard = ({ flight, totalPassengers, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${weekday} ${day} ${month} ${year}`;
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const toggleDetails = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-white rounded-none border border-[#ddd] mb-3 overflow-hidden shadow-sm w-full">
      <div className="p-3 h-[140px]">
        <div className="flex gap-3 h-full">
          {/* Aircraft Image - Left Side - 188px width */}
          <div className="w-[188px] h-full flex-shrink-0">
            <div className="rounded overflow-hidden h-full">
              <img
                src={
                  flight.aircraft_images && flight.aircraft_images.length > 0
                    ? getImageUrl(flight.aircraft_images[0].presigned_url || flight.aircraft_images[0].url)
                    : flight.aircraft?.image || '/no_aircraft.png'
                }
                alt="Aircraft"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = '/no_aircraft.png';
                }}
              />
            </div>
          </div>

          {/* Flight Details - Right Side */}
          <div className="flex-1 flex flex-col justify-between py-1">
            {/* Flight Number */}
            <div className="mb-2">
              <h3 className="text-[1.25rem] text-[#333] font-bold m-0 leading-none">
                {flight.flightNumber}
              </h3>
            </div>

            {/* Route Info with Select Button - Single Row */}
            <div className="flex items-center justify-between mb-2">
              {/* Departure */}
              <div className="flex-shrink-0">
                <h4 className="text-[0.875rem] font-semibold text-[#555] mb-0.5 leading-tight">
                  {flight.departure?.airport_code} {formatTime(flight.departure?.datetime)}
                </h4>
                <p className="mb-0 text-[0.75rem] text-[#777] leading-tight">
                  {formatDate(flight.departure?.datetime)}
                </p>
              </div>

              {/* Duration - Centered */}
              <div className="text-center flex flex-col justify-center items-center px-6">
                <span className="text-[1rem] text-[#555] mb-0.5">→</span>
                <p className="mb-0 text-[0.7rem] text-[#777] whitespace-nowrap leading-tight">
                  {flightSearchService.calculateDuration(flight.duration)}
                </p>
              </div>

              {/* Arrival */}
              <div className="flex-shrink-0 text-right">
                <h4 className="text-[0.875rem] font-semibold text-[#555] mb-0.5 leading-tight">
                  {flight.arrival?.airport_code} {formatTime(flight.arrival?.datetime)}
                </h4>
                <p className="mb-0 text-[0.75rem] text-[#777] leading-tight">
                  {formatDate(flight.arrival?.datetime)}
                </p>
              </div>

              {/* Select Button */}
              <div className="flex-shrink-0 ml-4">
                <button
                  onClick={() => onSelect(flight)}
                  className="bg-[#ffd200] hover:bg-[#e6bb00] text-black font-bold px-6 py-2 text-[0.8rem] shadow-none rounded leading-tight"
                >
                  Select
                </button>
              </div>
            </div>

            {/* Seats Available */}
            <div className="mb-2">
              <p className="text-[0.8rem] text-[#555] mb-0 leading-tight">
                Seats Available : <span className="font-semibold text-[#333]">{flight.availableSeats}</span>
              </p>
            </div>

            {/* Pricing and Before You Book */}
            <div className="flex justify-between items-center">
              <div className="flex gap-4">
                <p className="font-semibold text-[#333] text-[0.8rem] mb-0 leading-tight">
                  Fare/Pax <span className="font-bold">${flight.price?.toFixed(2)}</span>
                </p>
                <p className="font-semibold text-[#333] text-[0.8rem] mb-0 leading-tight">
                  Total Fare <span className="font-bold">${(flight.price * totalPassengers)?.toFixed(2)}</span>
                </p>
              </div>
              <div>
                <button
                  onClick={toggleDetails}
                  className="font-semibold text-[#555] text-[0.75rem] bg-transparent border-0 hover:text-[#333] cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  Before You Book
                  <i className={`fa fa-caret-down transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Details */}
      {isExpanded && (
        <BeforeYouBook
          isOpen={true}
          onToggle={toggleDetails}
        />
      )}
    </div>
  );
};

export default FlightCard;
